import { useState, useCallback } from 'react';
import { leaderApi } from '../api/leaderApi';

/**
 * Custom hook to manage the lifecycle of Leader tool data.
 * Handles fetching assignments, absences, workers, vehicles, and kolonnen.
 * Implements Rank 2 (personal_only) filtering logic.
 * 
 * @param {Object} options
 * @param {boolean} options.isReadOnly - Whether filtering for personal data is required
 * @param {string|number} options.workerId - The ID of the current user's worker record
 * @returns {Object} result
 */
export function useLeaderData({ isReadOnly, workerId }) {
    // Current visible data
    const [assignments, setAssignments] = useState([]);
    const [absences, setAbsences] = useState([]);
    
    // Global data for conflict detection (usually from today onwards or wider range)
    const [assignmentsForConflicts, setAssignmentsForConflicts] = useState([]);
    const [absencesForConflicts, setAbsencesForConflicts] = useState([]);
    
    // Complete list for Search/List view
    const [allAssignments, setAllAssignments] = useState([]);
    
    const [workers, setWorkers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [kolonnen, setKolonnen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [companyLoc, setCompanyLoc] = useState(null);


    /**
     * Internal helper to filter data based on Read-Only (Rank 2) permissions.
     */
    const filterForReadOnly = useCallback((data, type = 'assignments') => {
        if (!isReadOnly) return data;
        if (!workerId) return [];
        
        const wId = String(workerId);
        return type === 'assignments' 
            ? data.filter(a => (a.workers || []).some(w => String(w.worker_id) === wId))
            : data.filter(ab => String(ab.worker_id) === wId);
    }, [isReadOnly, workerId]);

    /**
     * Loads the initial data set and global conflict data.
     */
    const loadData = useCallback(async (dateRange = null, showLoader = false) => {
        try {
            // Only show loader if explicitly requested or if we have no assignments yet
            if (showLoader || (assignments.length === 0 && workers.length === 0)) {
                setLoading(true);
            }
            const [
                fetchedAssignments, 
                fetchedUndated,
                fetchedAbsences, 
                fetchedWorkers, 
                fetchedVehicles, 
                fetchedKolonnen,
                fetchedGlobalAssignments,
                fetchedGlobalAbsences,
                fetchedCategories,
                fetchedSettings
            ] = await Promise.all([
                leaderApi.getAssignments(dateRange),
                leaderApi.getAssignments({ undated: true }),
                leaderApi.getAbsences(dateRange),
                leaderApi.getWorkers(),
                leaderApi.getVehicles(),
                leaderApi.getKolonnen(),
                leaderApi.getAssignments({ start: new Date().toISOString().split('T')[0] }),
                leaderApi.getAbsences({ start: new Date().toISOString().split('T')[0] }),
                leaderApi.getVehicleCategories(),
                leaderApi.getSettings()
            ]);



            // Merge dated and undated assignments
            const combined = [...fetchedAssignments];
            (fetchedUndated || []).forEach(ua => {
                if (!combined.some(a => String(a.id) === String(ua.id))) {
                    combined.push(ua);
                }
            });

            setAssignments(filterForReadOnly(combined, 'assignments'));
            setAbsences(filterForReadOnly(fetchedAbsences, 'absences'));
            setWorkers(fetchedWorkers);
            setVehicles(fetchedVehicles);
            setKolonnen(fetchedKolonnen);
            
            const mergePool = (prev, incoming) => {
                const map = new Map(prev.map(i => [String(i.id), i]));
                incoming.forEach(i => map.set(String(i.id), i));
                return Array.from(map.values());
            };

            setAssignmentsForConflicts(prev => {
                const global = filterForReadOnly(fetchedGlobalAssignments, 'assignments');
                const local = filterForReadOnly(fetchedAssignments, 'assignments');
                return mergePool(prev, [...global, ...local]);
            });
            
            setAbsencesForConflicts(prev => {
                const global = filterForReadOnly(fetchedGlobalAbsences, 'absences');
                const local = filterForReadOnly(fetchedAbsences, 'absences');
                return mergePool(prev, [...global, ...local]);
            });

            setCategories(fetchedCategories || []);
            if (fetchedSettings?.default_location_address) {
                setCompanyLoc({
                    address: fetchedSettings.default_location_address,
                    lat: parseFloat(fetchedSettings.default_location_lat),
                    lng: parseFloat(fetchedSettings.default_location_lng)
                });
            }

        } catch (err) {
            console.error('[useLeaderData] Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    }, [filterForReadOnly]);

    /**
     * Specific loader for the ListView (all assignments).
     */
    const loadAllAssignments = useCallback(async () => {
        setListLoading(true);
        try {
            const all = await leaderApi.getAssignments();
            setAllAssignments(filterForReadOnly(all, 'assignments'));
        } catch (err) {
            console.error('[useLeaderData] Failed to load all assignments:', err);
        } finally {
            setListLoading(false);
        }
    }, [filterForReadOnly]);

    /**
     * Loads historical data to extend conflict detection range.
     */
    const loadHistoryData = useCallback(async () => {
        try {
            const todayStr = new Date().toISOString().split('T')[0];
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            const historyStartStr = threeMonthsAgo.toISOString().split('T')[0];
            
            const [pastAssignments, pastAbsences] = await Promise.all([
                leaderApi.getAssignments({ start: historyStartStr, end: todayStr }),
                leaderApi.getAbsences({ start: historyStartStr, end: todayStr })
            ]);

            const filteredPastA = filterForReadOnly(pastAssignments || [], 'assignments');
            const filteredPastAbs = filterForReadOnly(pastAbsences || [], 'absences');

            if (filteredPastA.length > 0) {
                setAssignmentsForConflicts(prev => {
                    const existingIds = new Set(prev.map(a => String(a.id)));
                    const newPast = filteredPastA.filter(a => !existingIds.has(String(a.id)));
                    return [...prev, ...newPast];
                });
            }
            if (filteredPastAbs.length > 0) {
                setAbsencesForConflicts(prev => {
                    const existingIds = new Set(prev.map(ab => String(ab.id))); // Fix: fetch.id could be null, but assignments usually have IDs
                    const newPast = filteredPastAbs.filter(ab => !existingIds.has(String(ab.id)));
                    return [...prev, ...newPast];
                });
            }
        } catch (err) {
            console.error('[useLeaderData] Failed to load history data:', err);
        }
    }, [filterForReadOnly]);

    return {
        assignments,
        setAssignments, // Expose for manual updates (e.g. sorting)
        absences,
        setAbsences,
        assignmentsForConflicts,
        setAssignmentsForConflicts,
        absencesForConflicts,
        setAbsencesForConflicts,
        allAssignments,
        setAllAssignments,
        workers,
        setWorkers,
        vehicles,
        setVehicles,
        kolonnen,
        loading,
        listLoading,
        loadData,
        loadAllAssignments,
        loadHistoryData,
        categories,
        companyLoc
    };
}

